import React from 'react';
import { useApp } from '@/context/AppContext';
import { PageHeader, Card } from '@/components/common';
import { FileText, Landmark, CreditCard, CheckCircle2 } from 'lucide-react';

const typeMeta = {
  doc: { icon: FileText, color: '#8B5CF6', bg: 'bg-violet-50', text: 'text-violet-600' },
  tax: { icon: Landmark, color: '#EF4444', bg: 'bg-rose-50', text: 'text-rose-600' },
  payment: { icon: CreditCard, color: '#F59E0B', bg: 'bg-amber-50', text: 'text-amber-600' },
  close: { icon: CheckCircle2, color: '#06B6D4', bg: 'bg-cyan-50', text: 'text-cyan-600' },
};

const Calendar = () => {
  const { t, tx, data, lang } = useApp();
  const weekdays = lang === 'es' ? ['L', 'M', 'X', 'J', 'V', 'S', 'D'] : ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  // June 2026 starts Monday, 30 days
  const firstOffset = 0;
  const daysInMonth = 30;
  const cells = [...Array(firstOffset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const juneObligations = data.obligations.filter((o) => tx(o.month).toLowerCase().startsWith(lang === 'es' ? 'jun' : 'jun'));
  const byDay = juneObligations.reduce((acc, o) => { (acc[o.day] ||= []).push(o); return acc; }, {});

  return (
    <div>
      <PageHeader title={t('calendar.title')} subtitle={t('calendar.subtitle')} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-6" data-testid="calendar-grid">
          <h3 className="font-heading font-bold text-lg text-zinc-900 mb-4">{lang === 'es' ? 'Junio 2026' : 'June 2026'}</h3>
          <div className="grid grid-cols-7 gap-1.5 text-center">
            {weekdays.map((w, i) => <div key={i} className="text-[11px] font-semibold text-zinc-400 uppercase pb-2">{w}</div>)}
            {cells.map((d, i) => {
              const evs = d ? byDay[d] : null;
              return (
                <div key={i} className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm relative ${d ? 'border border-zinc-100 hover:border-zinc-200 transition-colors' : ''}`} data-testid={d ? `cal-day-${d}` : undefined}>
                  {d && <span className={`${evs ? 'font-bold text-zinc-900' : 'text-zinc-500'}`}>{d}</span>}
                  {evs && (
                    <div className="flex gap-0.5 mt-1">
                      {evs.map((e) => <span key={e.id} className="w-1.5 h-1.5 rounded-full" style={{ background: typeMeta[e.type].color }} />)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-6" data-testid="calendar-upcoming">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-zinc-400 mb-4">{t('calendar.upcoming')}</p>
          <div className="space-y-3">
            {data.obligations.map((o) => {
              const m = typeMeta[o.type];
              return (
                <div key={o.id} className="flex items-start gap-3" data-testid={`obligation-${o.id}`}>
                  <div className={`w-9 h-9 rounded-lg ${m.bg} flex items-center justify-center shrink-0`}><m.icon className={`w-4 h-4 ${m.text}`} /></div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-800 leading-snug">{tx(o.title)}</p>
                    <p className="text-xs text-zinc-400">{o.day} {tx(o.month)} · {t(`calendar.type.${o.type}`)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Calendar;
